import type { TFunction } from "src/design-system/core/TranslationContext";
import { type OfferDiscount, OfferTypeEnum } from "src/types/offer";
import type { ItemNameCategoryDiscount } from "src/types/offer/ItemNameCategoryDiscount";
import type { OfferScenario } from "src/types/offer/OfferScenario";
import { capitalizeFirst } from "src/utils/formatters/strings";
import { MarketingActionVariantEnum } from "src/utils/variants";
import { AGENDA_OFFER_AGENDA_TK } from "src/views/agenda/agenda-tab-view/offer-agenda-tab/config";
import { AGENDA_TRANSLATION_NAMESPACE } from "src/views/agenda/config";
import { formatOfferDiscountItems } from "src/views/offer/utils/OfferListView.utils";
import type { FormatterOptions } from "./common";
import { FORMATTERS_TRANSLATION_NAMESPACE } from "./config";
import { type FormatPriceOptions, formatPrice } from "./money";
import { oldFormatRoundPercent } from "./numbers";

export const OFFERS_FORMATTERS_TK = "OFFERS_FORMATTERS_TK";

export function formatDiscount(
    scenario: {
        type: OfferTypeEnum;
        discount?: Pick<OfferDiscount, "flatDiscount" | "percentDiscount">;
        items?: ItemNameCategoryDiscount[];
    },
    options: FormatterOptions
): string {
    const { translateRaw, translateCommon } = options;

    const scenarioDiscount = scenario?.discount;
    const scenarioItemList = scenario?.items;
    const scenarioType = scenario?.type;

    if (scenarioType === OfferTypeEnum.Bogo) {
        return "1=1";
    } else if (!scenarioDiscount && !scenarioItemList) {
        return "—";
    } else if (scenarioType === OfferTypeEnum.FlatDelivery) {
        return capitalizeFirst(translateCommon("concepts.flat-rate"));
    } else if (scenarioType === OfferTypeEnum.DiscountByItem) {
        return translateRaw(
            `${FORMATTERS_TRANSLATION_NAMESPACE}:${OFFERS_FORMATTERS_TK}.discount-by-item`
        );
    } else if (scenarioDiscount?.flatDiscount) {
        return formatPrice(scenarioDiscount?.flatDiscount, options);
    } else if (scenarioDiscount?.percentDiscount) {
        return oldFormatRoundPercent(scenarioDiscount?.percentDiscount, {
            showOfferedForOneHundred: true,
            options,
        });
    } else {
        return capitalizeFirst(translateCommon("concepts.offered"));
    }
}

export function formatOfferMinBasket(
    scenario: Pick<OfferScenario, "discount"> | undefined,
    options: FormatPriceOptions
): string {
    return formatPrice(scenario?.discount?.minBasket ?? 0, { ...options, showHyphenIfZero: true });
}

export function formatOfferDiscountTooltip(
    scenario: OfferScenario | undefined,
    t: TFunction
): string {
    return scenario?.type === OfferTypeEnum.DiscountByItem
        ? formatOfferDiscountItems(scenario.items ?? [], t)
        : "";
}

export function formatOfferTypeToolTip(
    variants: MarketingActionVariantEnum[],
    scenario: Pick<OfferScenario, "discount"> | undefined,
    { translateRaw: t }: FormatterOptions
): string {
    const ns = `${AGENDA_TRANSLATION_NAMESPACE}:${AGENDA_OFFER_AGENDA_TK}`;
    const parts = [];

    if (
        variants.includes(MarketingActionVariantEnum.Special) &&
        variants.includes(MarketingActionVariantEnum.CoFunded)
    ) {
        parts.push(t(`${ns}.tooltip.is-co-funded-special-offer`));
    } else if (variants.includes(MarketingActionVariantEnum.Special)) {
        parts.push(t(`${ns}.tooltip.is-special-offer`));
    } else if (variants.includes(MarketingActionVariantEnum.CoFunded)) {
        parts.push(t(`${ns}.tooltip.is-co-funded-offer`));
    }

    if (
        variants.includes(MarketingActionVariantEnum.Special) &&
        scenario?.discount?.specialOperationTitle
    ) {
        parts.push(scenario.discount.specialOperationTitle);
    }

    return parts.join(" - ");
}
